import { useEffect, useState, memo } from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { AltArrowRight } from "@solar-icons/react/ssr";

import { request } from "@/Api";
import { WidgetHeaderRow, ListRowDivider } from "../primitives";

const DashboardComments = memo(() => {
    const theme = useTheme();
    const [comments, setComments] = useState(null);

    useEffect(() => {
        request
            .get("/dashboard/comments")
            .then((r) => setComments(r.data || []))
            .catch(() => setComments([]));
    }, []);

    const commentHref = (comment) => {
        const type = comment.model_type;
        const id = comment.model_instance_id;
        if (!type || !id) return "#";
        return `/${type}s/${id}`;
    };

    const commentSubtitle = (comment) => {
        const meta = comment.meta || {};
        const parts = [meta.project_name, meta.bid_name || meta.name].filter(
            Boolean,
        );
        if (parts.length) return parts.join(" • ");
        return comment.person_name || "";
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                height: "100%",
            }}
        >
            <WidgetHeaderRow
                title="Your Unresolved Comments"
                count={comments?.length ?? null}
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    flex: 1,
                    overflow: "auto",
                }}
            >
                {!comments &&
                    [1, 2, 3].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={44}
                            sx={{
                                borderRadius: "6px",
                                bgcolor: "action.hover",
                            }}
                        />
                    ))}
                {comments?.map((comment, i) => (
                    <Box
                        key={comment.id || i}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                        }}
                    >
                        {i > 0 && <ListRowDivider />}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "40px",
                                px: "8px",
                                py: "6px",
                                borderRadius: "6px",
                                height: 44,
                                "&:hover": { bgcolor: "action.hover" },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "2px",
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: "text.primary",
                                        fontFamily: "Inter Tight, sans-serif",
                                        lineHeight: 1.3,
                                        overflow: "auto",
                                        whiteSpace: "nowrap",
                                        scrollbarWidth: "none",
                                        "&::-webkit-scrollbar": {
                                            display: "none",
                                        },
                                    }}
                                >
                                    {comment.description}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: "text.secondary",
                                        fontFamily: "Inter Tight, sans-serif",
                                        lineHeight: 1.3,
                                        overflow: "auto",
                                        whiteSpace: "nowrap",
                                        scrollbarWidth: "none",
                                        "&::-webkit-scrollbar": {
                                            display: "none",
                                        },
                                    }}
                                >
                                    {commentSubtitle(comment)}
                                </Typography>
                            </Box>
                            <Box
                                component="a"
                                href={commentHref(comment)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    background: theme.palette.action.hover,
                                    borderRadius: "19px",
                                    height: 24,
                                    pl: "12px",
                                    pr: "10px",
                                    textDecoration: "none",
                                    flexShrink: 0,
                                    "&:hover": { bgcolor: "action.hover" },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: "text.primary",
                                        fontFamily: "Inter Tight, sans-serif",
                                        lineHeight: 1.3,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    See comment
                                </Typography>
                                <AltArrowRight
                                    size={10}
                                    color={theme.palette.text.secondary}
                                    weight="Outline"
                                />
                            </Box>
                        </Box>
                    </Box>
                ))}
                {comments?.length === 0 && (
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: "text.secondary",
                            fontFamily: "Inter Tight, sans-serif",
                            px: "8px",
                        }}
                    >
                        No unresolved comments
                    </Typography>
                )}
            </Box>
        </Box>
    );
});

DashboardComments.displayName = "DashboardComments";

export default DashboardComments;
